import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';

/**
 * Toast types for different message categories
 */
export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'danger',
  WARNING = 'warning',
  INFO = 'primary'
}

/**
 * Toast position options
 */
export enum ToastPosition {
  TOP = 'top',
  MIDDLE = 'middle',
  BOTTOM = 'bottom'
}

/**
 * Configuration options for toast notifications
 */
export interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
  position?: ToastPosition;
  showCloseButton?: boolean;
  translucent?: boolean;
  animated?: boolean;
  icon?: string;
  buttons?: Array<{
    text: string;
    role?: string;
    handler?: () => boolean | void;
  }>;
}

/**
 * Default toast configuration
 */
const DEFAULT_TOAST_CONFIG: Required<Omit<ToastOptions, 'message' | 'buttons'>> = {
  type: ToastType.INFO,
  duration: 3000,
  position: ToastPosition.BOTTOM,
  showCloseButton: false,
  translucent: true,
  animated: true,
  icon: ''
};

/**
 * Toast notification service that provides a consistent interface for showing
 * user feedback messages throughout the application using Ionic's ToastController.
 * 
 * Features:
 * - Type-based styling (success, error, warning, info)
 * - Configurable position and duration
 * - Support for custom icons and buttons
 * - Integration with error handler service
 * - Accessibility support
 * - Promise-based API for handling user interactions
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {

  private readonly toastController = inject(ToastController);

  /**
   * Shows a toast notification with the specified configuration
   * @param options - Toast configuration options
   * @returns Promise that resolves when toast is dismissed
   */
  async show(options: ToastOptions): Promise<void> {
    const config = { ...DEFAULT_TOAST_CONFIG, ...options };
    
    const toast = await this.toastController.create({
      message: config.message,
      duration: config.duration,
      position: config.position,
      color: config.type,
      translucent: config.translucent,
      animated: config.animated,
      icon: config.icon || this.getDefaultIcon(config.type),
      buttons: this.buildButtons(config),
      cssClass: this.buildCssClasses(config.type),
      // Accessibility
      htmlAttributes: {
        'aria-live': config.type === ToastType.ERROR ? 'assertive' : 'polite',
        'role': 'status'
      }
    });

    await toast.present();
  }

  /**
   * Shows a success toast notification
   * @param message - Success message to display
   * @param duration - Duration in milliseconds (optional)
   * @returns Promise that resolves when toast is dismissed
   */
  async showSuccess(message: string, duration?: number): Promise<void> {
    return this.show({
      message,
      type: ToastType.SUCCESS,
      duration: duration || 2500,
      icon: 'checkmark-circle-outline'
    });
  }

  /**
   * Shows an error toast notification
   * @param message - Error message to display
   * @param duration - Duration in milliseconds (optional)
   * @returns Promise that resolves when toast is dismissed
   */
  async showError(message: string, duration?: number): Promise<void> {
    return this.show({
      message,
      type: ToastType.ERROR,
      duration: duration || 4000,
      showCloseButton: true,
      icon: 'alert-circle-outline'
    });
  }

  /**
   * Shows a warning toast notification
   * @param message - Warning message to display
   * @param duration - Duration in milliseconds (optional)
   * @returns Promise that resolves when toast is dismissed
   */
  async showWarning(message: string, duration?: number): Promise<void> {
    return this.show({
      message,
      type: ToastType.WARNING,
      duration: duration || 3500,
      icon: 'warning-outline'
    });
  }

  /**
   * Shows an info toast notification
   * @param message - Info message to display
   * @param duration - Duration in milliseconds (optional)
   * @returns Promise that resolves when toast is dismissed
   */
  async showInfo(message: string, duration?: number): Promise<void> {
    return this.show({
      message,
      type: ToastType.INFO,
      duration: duration || 3000,
      icon: 'information-circle-outline'
    });
  }

  /**
   * Shows a loading toast that stays visible until manually dismissed
   * @param message - Loading message to display
   * @returns Promise with dismiss function
   */
  async showLoading(message: string = 'Loading...'): Promise<{ dismiss: () => Promise<void> }> {
    const toast = await this.toastController.create({
      message,
      duration: 0, // Indefinite duration
      position: ToastPosition.BOTTOM,
      color: ToastType.INFO,
      translucent: true,
      animated: true,
      icon: 'hourglass-outline',
      cssClass: 'toast-loading',
      htmlAttributes: {
        'aria-live': 'polite',
        'role': 'status'
      }
    });

    await toast.present();

    return {
      dismiss: async () => {
        await toast.dismiss();
      }
    };
  }

  /**
   * Shows a confirmation toast with action buttons
   * @param message - Confirmation message
   * @param confirmText - Text for confirm button
   * @param cancelText - Text for cancel button
   * @returns Promise that resolves to true if confirmed, false if cancelled
   */
  async showConfirmation(
    message: string,
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel'
  ): Promise<boolean> {
    return new Promise(async (resolve) => {
      const toast = await this.toastController.create({
        message,
        duration: 0, // Keep open until user interaction
        position: ToastPosition.MIDDLE,
        color: ToastType.WARNING,
        translucent: true,
        animated: true,
        icon: 'help-circle-outline',
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => {
              resolve(false);
            }
          },
          {
            text: confirmText,
            handler: () => {
              resolve(true);
            }
          }
        ],
        cssClass: 'toast-confirmation',
        htmlAttributes: {
          'aria-live': 'assertive',
          'role': 'alertdialog'
        }
      });

      await toast.present();
    });
  }

  /**
   * Dismisses all currently visible toasts
   * @returns Promise that resolves when all toasts are dismissed
   */
  async dismissAll(): Promise<void> {
    const activeToast = await this.toastController.getTop();
    if (activeToast) {
      await this.toastController.dismiss();
      // Recursively dismiss any remaining toasts
      await this.dismissAll();
    }
  }

  /**
   * Gets the default icon for a toast type
   * @param type - Toast type
   * @returns Icon name
   */
  private getDefaultIcon(type: ToastType): string {
    switch (type) {
      case ToastType.SUCCESS:
        return 'checkmark-circle-outline';
      case ToastType.ERROR:
        return 'alert-circle-outline';
      case ToastType.WARNING:
        return 'warning-outline';
      case ToastType.INFO:
      default:
        return 'information-circle-outline';
    }
  }

  /**
   * Builds buttons array including close button if needed
   * @param config - Toast configuration
   * @returns Buttons array
   */
  private buildButtons(config: Required<Omit<ToastOptions, 'message' | 'buttons'>> & Pick<ToastOptions, 'buttons'>): any[] {
    const buttons: any[] = [];

    // Add custom buttons first
    if (config.buttons) {
      buttons.push(...config.buttons);
    }

    // Add close button if requested
    if (config.showCloseButton) {
      buttons.push({
        text: 'Close',
        role: 'cancel',
        icon: 'close-outline'
      });
    }

    return buttons;
  }

  /**
   * Builds CSS classes for toast styling
   * @param type - Toast type
   * @returns CSS class string
   */
  private buildCssClasses(type: ToastType): string {
    const classes = ['app-toast'];
    
    switch (type) {
      case ToastType.SUCCESS:
        classes.push('toast-success');
        break;
      case ToastType.ERROR:
        classes.push('toast-error');
        break;
      case ToastType.WARNING:
        classes.push('toast-warning');
        break;
      case ToastType.INFO:
        classes.push('toast-info');
        break;
    }

    return classes.join(' ');
  }
}